import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../../components/commonDesign/Button'
import SydInput from '../../../../components/commonDesign/SydInput'
import Loading from '../../../../components/molecules/Loading'
import { useSubmitDenaliAction } from '../../../../api/denaliAssistant'

const OTHER_VALUE = '__other__'

const stateCache = new Map()

function getCachedState (actionTraceId, questions) {
  if (stateCache.has(actionTraceId)) {
    return stateCache.get(actionTraceId)
  }
  const initial = {
    currentIndex: 0,
    answers: buildInitialAnswersRaw(questions)
  }
  stateCache.set(actionTraceId, initial)
  return initial
}

function updateCachedState (actionTraceId, updates) {
  const current = stateCache.get(actionTraceId) || {}
  stateCache.set(actionTraceId, { ...current, ...updates })
}

function clearCachedState (actionTraceId) {
  stateCache.delete(actionTraceId)
}

function buildInitialAnswersRaw (questions) {
  const answers = {}
  for (const q of questions) {
    answers[q.id] = { selected: [], otherText: '' }
  }
  return answers
}

const useStyles = makeStyles(() => ({
  container: {
    margin: '16px 0',
    padding: '20px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--dai-color-surface4)',
    fontFamily: 'var(--font-sans)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box'
  },
  title: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    marginBottom: '16px',
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.4
  },
  progressBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '20px'
  },
  progressDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-neutral-300)',
    transition: 'all 0.2s ease'
  },
  progressDotActive: {
    backgroundColor: 'var(--color-primary-500)',
    transform: 'scale(1.25)'
  },
  progressDotCompleted: {
    backgroundColor: 'var(--color-primary-400)'
  },
  progressText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    marginLeft: 'auto'
  },
  questionText: {
    fontSize: 'var(--font-size-md)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    marginBottom: '4px',
    lineHeight: 1.5
  },
  questionDescription: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-neutral-600)',
    marginBottom: '16px',
    lineHeight: 1.5
  },
  selectionHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-500)',
    marginBottom: '12px'
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px'
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-neutral-050)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: 'var(--color-primary-400)',
      backgroundColor: 'var(--color-primary-050)',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    }
  },
  optionSelected: {
    borderColor: 'var(--color-primary-500)',
    backgroundColor: 'var(--color-primary-050)'
  },
  optionDisabled: {
    opacity: 0.5,
    cursor: 'default',
    pointerEvents: 'none'
  },
  indicator: {
    width: '18px',
    height: '18px',
    marginTop: '1px',
    borderRadius: '50%',
    border: '2px solid var(--color-neutral-400)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease'
  },
  indicatorCheckbox: {
    borderRadius: '3px'
  },
  indicatorSelected: {
    backgroundColor: 'var(--color-primary-500)',
    borderColor: 'var(--color-primary-500)'
  },
  indicatorDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'white'
  },
  checkmark: {
    color: 'white',
    fontSize: '11px',
    lineHeight: 1
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1
  },
  optionLabel: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--dai-color-text)',
    lineHeight: 1.4
  },
  optionDescription: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-600)',
    lineHeight: 1.4
  },
  otherInput: {
    marginTop: '4px',
    marginLeft: '42px',
    marginRight: 0
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  navLeft: {
    display: 'flex',
    gap: '8px'
  },
  navRight: {
    display: 'flex',
    gap: '8px'
  },
  submitError: {
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    marginBottom: '12px'
  },
  successContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0'
  },
  successMessage: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--dai-color-text)',
    fontFamily: 'var(--font-sans)'
  },
  collapsed: {
    padding: '16px 20px',
    transition: 'height 0.3s ease-out'
  },
  loadingOverlay: {
    position: 'relative',
    opacity: 0.6,
    pointerEvents: 'none'
  },
  loadingIndicator: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
  }
}))

function QuestionsRenderer ({ data, message }) {
  const classes = useStyles()
  const submitAction = useSubmitDenaliAction()

  const cached = getCachedState(data.actionTraceId, data.questions)
  const [currentIndex, setCurrentIndex] = useState(cached.currentIndex)
  const [answers, setAnswers] = useState(cached.answers)
  const [submitError, setSubmitError] = useState(null)

  const isComplete = message?.data?.actions?.[data.actionTraceId]?.actionStatus === 'complete'
  const submitting = submitAction.isLoading
  const questions = data.questions
  const currentQuestion = questions[currentIndex]
  const isMultiple = currentQuestion?.selectionType === 'multiple'
  const isLastQuestion = currentIndex === questions.length - 1
  const currentAnswer = answers[currentQuestion?.id]

  const hasAnswer = useMemo(() => {
    if (!currentAnswer) return false
    const hasRegularSelection = currentAnswer.selected.some(v => v !== OTHER_VALUE)
    const hasOtherAnswer = currentAnswer.selected.includes(OTHER_VALUE) && currentAnswer.otherText.trim().length > 0
    return hasRegularSelection || hasOtherAnswer
  }, [currentAnswer])

  const otherIncomplete = currentAnswer?.selected.includes(OTHER_VALUE) && !currentAnswer.otherText.trim()

  const canProceed = useMemo(() => {
    if (!currentQuestion) return false
    if (otherIncomplete) return false
    if (currentQuestion.required) return hasAnswer
    return true
  }, [currentQuestion, hasAnswer, otherIncomplete])

  const handleSelectOption = useCallback((value) => {
    if (submitting) return
    setAnswers(prev => {
      const questionId = currentQuestion.id
      const current = prev[questionId]
      let newSelected

      if (isMultiple) {
        newSelected = current.selected.includes(value)
          ? current.selected.filter(v => v !== value)
          : [...current.selected, value]
      } else {
        newSelected = current.selected.includes(value) ? [] : [value]
      }

      const updated = { ...prev, [questionId]: { ...current, selected: newSelected } }
      updateCachedState(data.actionTraceId, { answers: updated })
      return updated
    })
  }, [currentQuestion, isMultiple, submitting, data.actionTraceId])

  const handleOtherTextChange = useCallback((text) => {
    if (submitting) return
    setAnswers(prev => {
      const questionId = currentQuestion.id
      const current = prev[questionId]
      const updated = { ...prev, [questionId]: { ...current, otherText: text } }
      updateCachedState(data.actionTraceId, { answers: updated })
      return updated
    })
  }, [currentQuestion, submitting, data.actionTraceId])

  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      const next = currentIndex + 1
      setCurrentIndex(next)
      updateCachedState(data.actionTraceId, { currentIndex: next })
    }
  }, [currentIndex, questions.length, data.actionTraceId])

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      const prev = currentIndex - 1
      setCurrentIndex(prev)
      updateCachedState(data.actionTraceId, { currentIndex: prev })
    }
  }, [currentIndex, data.actionTraceId])

  const handleSkip = useCallback(() => {
    setAnswers(prev => {
      const updated = { ...prev, [currentQuestion.id]: { selected: [], otherText: '' } }
      updateCachedState(data.actionTraceId, { answers: updated })
      return updated
    })
    if (!isLastQuestion) {
      const next = currentIndex + 1
      setCurrentIndex(next)
      updateCachedState(data.actionTraceId, { currentIndex: next })
    }
  }, [currentQuestion, isLastQuestion, currentIndex, data.actionTraceId])

  const handleSubmit = useCallback(async () => {
    setSubmitError(null)

    const summaryItems = questions.map(q => {
      const answer = answers[q.id]
      if (!answer || answer.selected.length === 0) {
        return { id: q.id, question: q.text, answer: [], values: [], skipped: true }
      }
      const selectedValues = answer.selected.filter(v => v !== OTHER_VALUE)
      const labels = selectedValues.map(v => {
        const opt = q.options.find(o => o.value === v)
        return opt ? opt.label : v
      })
      const item = { id: q.id, question: q.text, answer: labels, values: selectedValues, skipped: false }
      if (answer.selected.includes(OTHER_VALUE) && answer.otherText.trim()) {
        item.other = answer.otherText.trim()
      }
      return item
    })

    const summaryJson = JSON.stringify([{
      type: 'questionnaire-answers',
      label: data.label,
      questions: summaryItems
    }])

    const prompt = '```denali-ai-actions\n' + summaryJson + '\n```'

    try {
      await submitAction.mutateAsync({
        actionTraceId: data.actionTraceId,
        input: { completed: true, answers: summaryItems },
        message,
        prompt,
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        }
      })
      clearCachedState(data.actionTraceId)
    } catch (error) {
      setSubmitError(error?.message || error?.toString() || 'Failed to submit answers. Please try again.')
    }
  }, [answers, data.actionTraceId, data.label, message, questions, submitAction])

  if (isComplete) {
    return (
      <div className={clsx(classes.container, classes.collapsed)}>
        <div className={classes.successContainer}>
          <Icon
            name={ICON_NAMES.checkCircle}
            customSize='20px'
            style={{ color: 'var(--color-success)' }}
          />
          <div className={classes.successMessage}>
            {data.label} completed
          </div>
        </div>
      </div>
    )
  }

  if (!currentQuestion) return null

  return (
    <div className={classes.container}>
      <div className={classes.title}>{data.label}</div>

      {questions.length > 1 && (
        <div className={classes.progressBar}>
          {questions.map((_, idx) => (
            <div
              key={idx}
              className={clsx(
                classes.progressDot,
                idx === currentIndex && classes.progressDotActive,
                idx < currentIndex && classes.progressDotCompleted
              )}
            />
          ))}
          <span className={classes.progressText}>
            {currentIndex + 1} of {questions.length}
          </span>
        </div>
      )}

      {submitError && (
        <div className={classes.submitError}>{submitError}</div>
      )}

      <div className={submitting ? classes.loadingOverlay : undefined}>
        {submitting && (
          <div className={classes.loadingIndicator}>
            <Loading />
          </div>
        )}

        <div className={classes.questionText}>{currentQuestion.text}</div>
        {currentQuestion.description && (
          <div className={classes.questionDescription}>{currentQuestion.description}</div>
        )}

        {isMultiple && (
          <div className={classes.selectionHint}>Select all that apply</div>
        )}

        <div className={classes.optionsContainer}>
          {currentQuestion.options.map((option) => {
            const isSelected = currentAnswer.selected.includes(option.value)
            return (
              <div
                key={option.value}
                className={clsx(
                  classes.option,
                  isSelected && classes.optionSelected,
                  submitting && classes.optionDisabled
                )}
                onClick={() => handleSelectOption(option.value)}
              >
                <div
                  className={clsx(
                    classes.indicator,
                    isMultiple && classes.indicatorCheckbox,
                    isSelected && classes.indicatorSelected
                  )}
                >
                  {isSelected && (
                    isMultiple
                      ? <span className={classes.checkmark}>✓</span>
                      : <div className={classes.indicatorDot} />
                  )}
                </div>
                <div className={classes.optionContent}>
                  <span className={classes.optionLabel}>{option.label}</span>
                  {option.description && (
                    <span className={classes.optionDescription}>{option.description}</span>
                  )}
                </div>
              </div>
            )
          })}

          {currentQuestion.allowOther && (
            <>
              <div
                className={clsx(
                  classes.option,
                  currentAnswer.selected.includes(OTHER_VALUE) && classes.optionSelected,
                  submitting && classes.optionDisabled
                )}
                onClick={() => handleSelectOption(OTHER_VALUE)}
              >
                <div
                  className={clsx(
                    classes.indicator,
                    isMultiple && classes.indicatorCheckbox,
                    currentAnswer.selected.includes(OTHER_VALUE) && classes.indicatorSelected
                  )}
                >
                  {currentAnswer.selected.includes(OTHER_VALUE) && (
                    isMultiple
                      ? <span className={classes.checkmark}>✓</span>
                      : <div className={classes.indicatorDot} />
                  )}
                </div>
                <div className={classes.optionContent}>
                  <span className={classes.optionLabel}>Other</span>
                </div>
              </div>

              {currentAnswer.selected.includes(OTHER_VALUE) && (
                <div className={classes.otherInput}>
                  <SydInput
                    value={currentAnswer.otherText}
                    onChange={(e) => handleOtherTextChange(e.target ? e.target.value : e)}
                    placeholder='Type your answer...'
                    disabled={submitting}
                    type='input'
                    minWidth='0'
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className={classes.navigation}>
          <div className={classes.navLeft}>
            {currentIndex > 0 && (
              <SydButton
                variant='ghost'
                onClick={handleBack}
                disabled={submitting}
                size='sm'
              >
                Back
              </SydButton>
            )}
          </div>
          <div className={classes.navRight}>
            {!currentQuestion.required && (
              <SydButton
                variant='ghost'
                onClick={handleSkip}
                disabled={submitting}
                size='sm'
              >
                {isLastQuestion ? 'Clear answer' : 'Skip'}
              </SydButton>
            )}
            {isLastQuestion ? (
              <SydButton
                variant='primary'
                onClick={handleSubmit}
                disabled={submitting || !canSubmitAll(questions, answers)}
                processing={submitting}
                size='sm'
              >
                Submit
              </SydButton>
            ) : (
              <SydButton
                variant='primary'
                onClick={handleNext}
                disabled={!canProceed}
                size='sm'
              >
                Next
              </SydButton>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function canSubmitAll (questions, answers) {
  for (const question of questions) {
    const answer = answers[question.id]
    if (answer?.selected.includes(OTHER_VALUE) && !answer.otherText.trim()) return false
    if (!question.required) continue
    if (!answer || answer.selected.length === 0) return false
  }
  return true
}

QuestionsRenderer.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    actionTraceId: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      description: PropTypes.string,
      selectionType: PropTypes.oneOf(['single', 'multiple']),
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        description: PropTypes.string
      })).isRequired,
      allowOther: PropTypes.bool,
      required: PropTypes.bool
    })).isRequired
  }).isRequired,
  message: PropTypes.object
}

export default QuestionsRenderer
